import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, Clock, Coins, Ruler, BookOpen, Scissors, AlertCircle, ArrowRight, ArrowDown, Calculator, Divide, Layers, Zap, Info, Landmark, HelpCircle, CheckCircle2, PlusCircle, MinusCircle, Box, Droplets, Calendar, TrendingUp, Search, Percent, Star, ArrowUpCircle, ArrowDownCircle, MoveRight, Type, Waves, Sparkles, Wand2 } from 'lucide-react';
import { ModuleId } from '../../types';

// --- DIDACTISCHE UI COMPONENTEN ---

const LessonSection = ({
  title,
  icon: Icon,
  color,
  children
}: {
  title: string;
  icon: any;
  color: string;
  children: React.ReactNode;
}) => <section className={`mb-12 border-2 border-${color}-100 rounded-[2rem] overflow-hidden bg-white shadow-xl`} data-id="element-652">
    <div className={`bg-${color}-600 p-6 text-white flex items-center gap-4`} data-id="element-653">
      <div className={`p-3 bg-white/20 rounded-2xl`} data-id="element-654">
        <Icon className="w-8 h-8" data-id="element-655" />
      </div>
      <div data-id="element-656">
        <h4 className="text-2xl font-black tracking-tight uppercase" data-id="element-657">{title}</h4>
        <div className="h-1 w-20 bg-white/40 rounded-full mt-1" data-id="element-658" />
      </div>
    </div>
    <div className="p-8 space-y-8" data-id="element-659">
      {children}
    </div>
  </section>;
const TheoryCard = ({
  title,
  children,
  color = "slate"
}: {
  title: string;
  children: React.ReactNode;
  color?: string;
}) => <div className={`bg-${color}-50 border-2 border-${color}-200 rounded-3xl p-6 relative`} data-id="element-660">
    <h5 className={`font-black text-${color}-900 text-sm uppercase tracking-widest mb-4 flex items-center gap-2`} data-id="element-661">
      <div className={`w-2 h-2 rounded-full bg-${color}-500`} data-id="element-662" /> {title}
    </h5>
    <div className="text-slate-700 text-sm leading-relaxed" data-id="element-663">
      {children}
    </div>
  </div>;
const StepByStepAction = ({
  step,
  title,
  content,
  color
}: {
  step: number;
  title: string;
  content: string;
  color: string;
}) => <div className="flex gap-4 group" data-id="element-664">
    <div className="flex flex-col items-center" data-id="element-665">
      <div className={`w-10 h-10 rounded-2xl bg-${color}-100 text-${color}-700 flex items-center justify-center font-black text-lg border-2 border-${color}-200 group-hover:scale-110 transition-transform`} data-id="element-666">
        {step}
      </div>
      <div className={`w-0.5 h-full bg-${color}-100 my-1`} data-id="element-667" />
    </div>
    <div className="pb-8" data-id="element-668">
      <h6 className="font-extrabold text-slate-900 mb-1" data-id="element-669">{title}</h6>
      <p className="text-slate-600 text-xs leading-relaxed italic" data-id="element-670">{content}</p>
    </div>
  </div>;
const ExceptionWarning = ({
  title,
  message
}: {
  title: string;
  message: string;
}) => <div className="bg-rose-50 border-2 border-rose-100 rounded-2xl p-4 flex gap-4 items-start" data-id="element-671">
    <div className="bg-rose-500 p-2 rounded-xl text-white shadow-lg shadow-rose-200" data-id="element-672">
      <AlertCircle className="w-5 h-5" data-id="element-673" />
    </div>
    <div data-id="element-674">
      <h6 className="font-black text-rose-900 text-xs uppercase tracking-tight" data-id="element-675">{title}</h6>
      <p className="text-rose-800 text-[11px] leading-relaxed mt-0.5" data-id="element-676">{message}</p>
    </div>
  </div>;
const ExampleBox = ({
  children
}: {
  children: React.ReactNode;
}) => <div className="bg-white border-2 border-slate-100 rounded-2xl p-4 shadow-sm font-mono text-sm text-slate-600 flex flex-wrap gap-4 items-center justify-center" data-id="element-677">
    {children}
  </div>;

// --- HOOFD COMPONENT ---

export function ModuleCheatSheetContent({
  moduleId
}: {
  moduleId: ModuleId;
}) {
  switch (moduleId) {
    case 'Metriek':
      return <div className="max-w-5xl mx-auto py-8" data-id="element-678">
          <header className="mb-12 text-center" data-id="element-679">
            <h2 className="text-5xl font-black text-indigo-950 tracking-tighter mb-4" data-id="element-680">METRIEK & MEETKUNDE</h2>
            <p className="text-xl text-slate-500 font-medium italic" data-id="element-681">"Hoe groot, hoe ver en hoeveel past erin?"</p>
          </header>

          <LessonSection title="1. Het Omrekensysteem" icon={TrendingUp} color="indigo" data-id="element-682">
            <TheoryCard title="De Trap van de Maten" color="indigo" data-id="element-683">
              <p className="mb-6" data-id="element-684">Om maten om te rekenen van groot naar klein, gebruiken we de trap. Onthoud het zinnetje: <strong data-id="element-685">Kan Het Dammetje Met De Centimeter Meten?</strong></p>
              
              

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8" data-id="element-686">
                <div className="space-y-4" data-id="element-687">
                  <StepByStepAction color="emerald" step={1} title="Trap Omlaag (×10)" content="Bij elke trede omlaag vermenigvuldig je met 10. Er komt een nul bij of de komma schuift naar rechts." data-id="element-688" />
                  <StepByStepAction color="emerald" step={2} title="Voorbeeld" content="5 meter naar centimeter? Dat zijn 2 treden omlaag. 5 -> 50 -> 500 cm." data-id="element-689" />
                </div>
                <div className="space-y-4" data-id="element-690">
                  <StepByStepAction color="rose" step={1} title="Trap Omhoog (÷10)" content="Bij elke trede omhoog deel je door 10. Er gaat een nul af of de komma schuift naar links." data-id="element-691" />
                  <StepByStepAction color="rose" step={2} title="Voorbeeld" content="3000 meter naar kilometer? Dat zijn 3 treden omhoog. 3000 -> 300 -> 30 -> 3 km." data-id="element-692" />
                </div>
              </div>
            </TheoryCard>

            <TheoryCard title="Speciale Maten: Gewicht & Vloeistof" color="sky" data-id="element-693">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-694">
                <div data-id="element-695">
                  <h6 className="font-bold text-sky-900 mb-2" data-id="element-696">Gewicht (Grammen)</h6>
                  <ExampleBox data-id="element-697">
                    <span className="font-bold" data-id="element-698">1 kg = 1000 g</span>
                    <span className="text-slate-300" data-id="element-699">|</span>
                    <span className="font-bold" data-id="element-700">1 g = 1000 mg</span>
                  </ExampleBox>
                </div>
                <div data-id="element-701">
                  <h6 className="font-bold text-sky-900 mb-2" data-id="element-702">Vloeistof (Liters)</h6>
                  <ExampleBox data-id="element-703">
                    <span className="font-bold" data-id="element-704">1 L = 10 dL = 100 cL = 1000 mL</span>
                  </ExampleBox>
                </div>
              </div>
            </TheoryCard>
          </LessonSection>

          <LessonSection title="2. Oppervlakte & Omtrek" icon={Box} color="emerald" data-id="element-705">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-706">
              <div className="space-y-6" data-id="element-707">
                <TheoryCard title="Omtrek (Lopen)" color="emerald" data-id="element-708">
                  <p className="mb-4" data-id="element-709">De omtrek is de buitenrand van een vorm. Denk aan de lengte van een hek om een weiland.</p>
                  <div className="bg-white rounded-2xl p-6 border-4 border-emerald-400 text-center" data-id="element-710">
                    <div className="mb-2 text-xs font-black text-slate-400 uppercase" data-id="element-711">Tel alle zijden op</div>
                    <div className="text-xl font-mono font-black text-emerald-700" data-id="element-712">Zijde + Zijde + Zijde + Zijde</div>
                  </div>
                </TheoryCard>
                <ExceptionWarning title="Verschillende eenheden" message="Als de ene kant in meters is en de andere in centimeters, moet je ze eerst gelijk maken voordat je ze optelt!" data-id="element-713" />
              </div>

              <div className="space-y-6" data-id="element-714">
                <TheoryCard title="Oppervlakte (Tegels)" color="teal" data-id="element-715">
                  <p className="mb-4" data-id="element-716">De oppervlakte is de ruimte binnen de lijnen. Hoeveel tegels heb je nodig om de vloer te bedekken?</p>
                  <div className="bg-white rounded-2xl p-6 border-4 border-teal-400 text-center" data-id="element-717">
                    <div className="mb-2 text-xs font-black text-slate-400 uppercase" data-id="element-718">Formule</div>
                    <div className="text-xl font-mono font-black text-teal-700 underline decoration-teal-200 decoration-4" data-id="element-719">LENGTE × BREEDTE</div>
                  </div>
                </TheoryCard>
                <div className="bg-teal-900 text-white p-4 rounded-2xl text-center shadow-lg" data-id="element-720">
                  <p className="text-[10px] uppercase font-black opacity-60" data-id="element-721">Het resultaat is altijd</p>
                  <p className="text-2xl font-black" data-id="element-722">m² <span className="text-sm font-light opacity-50 ml-2" data-id="element-723">of cm²</span></p>
                </div>
              </div>
            </div>
          </LessonSection>

          <LessonSection title="3. Inhoud & Volume" icon={Droplets} color="sky" data-id="element-724">
            <TheoryCard title="De 3D Formule" color="sky" data-id="element-725">
              <p className="mb-6" data-id="element-726">Inhoud bereken je bij voorwerpen waar iets <strong data-id="element-727">in</strong> kan, zoals een doos of een aquarium. Je rekent met 3 maten.</p>
              <div className="flex flex-col md:flex-row gap-8 items-center justify-center bg-white p-8 rounded-[2.5rem] border-2 border-sky-100 shadow-inner" data-id="element-728">
                <div className="text-center" data-id="element-729">
                  <div className="text-4xl font-black text-sky-600 tracking-tighter" data-id="element-730">L × B × H</div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase mt-2 tracking-widest" data-id="element-731">Lengte × Breedte × Hoogte</div>
                </div>
                <ArrowRight className="w-8 h-8 text-sky-200 hidden md:block" data-id="element-732" />
                <div className="bg-sky-600 text-white p-4 rounded-2xl font-black text-xl shadow-lg" data-id="element-733">Antwoord in m³ <span className="text-xs font-medium block opacity-70" data-id="element-734">(kubieke meter)</span></div>
              </div>
            </TheoryCard>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-735">
              {[{
              u: "1 dm³",
              v: "1 Liter",
              d: "Een pak melk"
            }, {
              u: "1 cm³",
              v: "1 milliliter",
              d: "Een kleine druppel"
            }, {
              u: "1 m³",
              v: "1000 Liter",
              d: "Een grote kuub water"
            }].map((item, i) => <div key={i} className="bg-white p-4 rounded-3xl border border-sky-100 text-center shadow-sm" data-id="element-736">
                  <div className="text-lg font-black text-sky-900" data-id="element-737">{item.u}</div>
                  <div className="text-sky-500 font-bold" data-id="element-738">= {item.v}</div>
                  <div className="text-[10px] text-slate-400 uppercase mt-2" data-id="element-739">{item.d}</div>
                </div>)}
            </div>
          </LessonSection>
        </div>;
    case 'Getalbegrip':
      return <div className="max-w-5xl mx-auto py-8" data-id="element-740">
          <header className="mb-12 text-center" data-id="element-741">
            <h2 className="text-5xl font-black text-orange-950 tracking-tighter mb-4" data-id="element-742">GETALBEGRIP & BREUKEN</h2>
            <p className="text-xl text-slate-500 font-medium italic" data-id="element-743">"Grote getallen, kommagetallen en eerlijk delen."</p>
          </header>

          <LessonSection title="1. Grote Getallen" icon={Search} color="orange" data-id="element-744">
            <TheoryCard title="Het Positieschema" color="orange" data-id="element-745">
              <p className="mb-6" data-id="element-746">Om getallen tot een <strong data-id="element-747">miljoen</strong> te kunnen lezen, kijken we naar de plek van elk cijfer. Elk cijfer heeft een eigen waarde.</p>
              
              <div className="grid grid-cols-7 gap-2 overflow-x-auto pb-4" data-id="element-748">
                {[{
                l: 'M',
                n: '1',
                d: 'Miljoen'
              }, {
                l: 'HD',
                n: '2',
                d: 'Honderd-duizend'
              }, {
                l: 'TD',
                n: '5',
                d: 'Tien-duizend'
              }, {
                l: 'D',
                n: '0',
                d: 'Duizend'
              }, {
                l: 'H',
                n: '6',
                d: 'Honderd'
              }, {
                l: 'T',
                n: '4',
                d: 'Tiental'
              }, {
                l: 'E',
                n: '8',
                d: 'Eenheid'
              }].map((item, i) => <div key={i} className="min-w-[80px] text-center space-y-2" data-id="element-749">
                    <div className="text-[10px] font-black text-slate-400 uppercase" data-id="element-750">{item.l}</div>
                    <div className="bg-white border-4 border-orange-100 rounded-2xl py-4 font-mono font-black text-2xl text-orange-600 shadow-sm" data-id="element-751">{item.n}</div>
                    <div className="text-[8px] font-bold text-slate-500 leading-none h-4" data-id="element-752">{item.d}</div>
                  </div>)}
              </div>
              
              <div className="mt-8 bg-orange-600 text-white p-6 rounded-[2rem] text-center shadow-xl" data-id="element-753">
                <p className="text-xs font-bold opacity-80 uppercase mb-1 tracking-widest" data-id="element-754">Je leest dit als:</p>
                <p className="text-xl font-extrabold italic" data-id="element-755">"Eén miljoen, tweehonderdvijftigduizend zeshonderdachtenveertig"</p>
              </div>
            </TheoryCard>
          </LessonSection>

          <LessonSection title="2. Kolomsgewijs Aftrekken" icon={MinusCircle} color="rose" data-id="element-756">
            <div className="flex flex-col lg:flex-row gap-12 items-start" data-id="element-757">
              <div className="w-full lg:w-1/3 bg-white p-10 rounded-[3rem] shadow-2xl border-2 border-rose-50 relative overflow-hidden font-mono" data-id="element-758">
                <div className="absolute top-0 left-0 w-2 h-full bg-rose-500" data-id="element-759" />
                <div className="text-4xl text-right space-y-4" data-id="element-760">
                  <div className="text-rose-300 text-sm absolute top-4 right-16 font-black" data-id="element-761">3 13</div>
                  <div className="flex justify-end gap-8 border-b-4 border-slate-900 pb-4" data-id="element-762">
                    <span className="text-slate-200 line-through decoration-rose-400 decoration-8" data-id="element-763">4</span>
                    <span className="font-black text-slate-800" data-id="element-764">3</span>
                  </div>
                  <div className="font-black text-slate-800 pr-4" data-id="element-765">- 1 8</div>
                  <div className="text-rose-600 font-black border-t-2 border-slate-100 pt-4" data-id="element-766">2 5</div>
                </div>
              </div>
              
              <div className="flex-1 space-y-2" data-id="element-767">
                <TheoryCard title="Het Lenen-Stappenplan" color="rose" data-id="element-768">
                  <StepByStepAction color="rose" step={1} title="Check de eenheden" content="3 min 8 kan niet. Je hebt er 3, je moet er 8 weggeven. Dat is te weinig!" data-id="element-769" />
                  <StepByStepAction color="rose" step={2} title="Klop bij de buren" content="Ga naar de tientallen (de 4). Leen er eentje. De 4 wordt nu een 3." data-id="element-770" />
                  <StepByStepAction color="rose" step={3} title="Toveren met 10" content="Die 1 die je leent, is eigenlijk 10 eenheden waard. De 3 wordt nu 13." data-id="element-771" />
                  <StepByStepAction color="rose" step={4} title="Reken uit" content="Nu kan het wel: 13 - 8 = 5. En dan de tientallen: 3 - 1 = 2. Totaal: 25." data-id="element-772" />
                </TheoryCard>
              </div>
            </div>
          </LessonSection>

          <LessonSection title="3. Alles over Breuken" icon={Divide} color="amber" data-id="element-773">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-774">
              <TheoryCard title="De Breuk-Naam" color="amber" data-id="element-775">
                <div className="flex flex-col items-center gap-6 py-4" data-id="element-776">
                  <div className="text-center" data-id="element-777">
                    <div className="text-4xl font-black text-amber-600" data-id="element-778">3</div>
                    <div className="w-20 h-1.5 bg-slate-900 rounded-full my-2" data-id="element-779" />
                    <div className="text-4xl font-black text-slate-800" data-id="element-780">4</div>
                  </div>
                  <div className="space-y-3 w-full" data-id="element-781">
                    <div className="bg-white p-3 rounded-xl border-l-4 border-amber-500 shadow-sm flex justify-between items-center" data-id="element-782">
                      <span className="font-black text-xs" data-id="element-783">TELLER</span>
                      <span className="text-[10px] text-slate-400" data-id="element-784">Hoeveel stukken heb je?</span>
                    </div>
                    <div className="bg-white p-3 rounded-xl border-l-4 border-slate-900 shadow-sm flex justify-between items-center" data-id="element-785">
                      <span className="font-black text-xs" data-id="element-786">NOEMER</span>
                      <span className="text-[10px] text-slate-400" data-id="element-787">Hoeveel stukken totaal?</span>
                    </div>
                  </div>
                </div>
              </TheoryCard>

              <div className="space-y-6" data-id="element-788">
                <TheoryCard title="Vereenvoudigen" color="orange" data-id="element-789">
                  <p className="text-xs mb-4" data-id="element-790">Maak de getallen van de breuk zo klein mogelijk door ze door hetzelfde getal te delen.</p>
                  <ExampleBox data-id="element-791">
                    <div className="flex items-center gap-4" data-id="element-792">
                      <div className="flex flex-col items-center font-bold" data-id="element-793"><span data-id="element-794">4</span><div className="w-4 h-0.5 bg-slate-900" data-id="element-795" /><span data-id="element-796">8</span></div>
                      <ArrowRight className="text-orange-400" data-id="element-797" />
                      <div className="text-[10px] font-black text-orange-600" data-id="element-798">DEEL DOOR 4</div>
                      <ArrowRight className="text-orange-400" data-id="element-799" />
                      <div className="flex flex-col items-center font-bold" data-id="element-800"><span data-id="element-801">1</span><div className="w-4 h-0.5 bg-slate-900" data-id="element-802" /><span data-id="element-803">2</span></div>
                    </div>
                  </ExampleBox>
                </TheoryCard>
                
                <ExceptionWarning title="Optellen van breuken" message="Bij 1/4 + 2/4 tel je alleen de bovenkant op. De onderkant blijft altijd hetzelfde! Het antwoord is dus 3/4, niet 3/8." data-id="element-804" />
              </div>
            </div>
          </LessonSection>
        </div>;
    case 'TijdGeld':
      return <div className="max-w-5xl mx-auto py-8" data-id="element-805">
          <header className="mb-12 text-center" data-id="element-806">
            <h2 className="text-5xl font-black text-emerald-950 tracking-tighter mb-4" data-id="element-807">TIJD & GELD</h2>
            <p className="text-xl text-slate-500 font-medium italic" data-id="element-808">"Seconden tellen en gepast betalen."</p>
          </header>

          <LessonSection title="1. Klokkijken (Analoog & Digitaal)" icon={Clock} color="emerald" data-id="element-809">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-810">
              <TheoryCard title="De Analoge Klok" color="emerald" data-id="element-811">
                <p className="mb-4" data-id="element-812">Een klok met wijzers werkt met kwartieren, halve uren en seconden.</p>
                <div className="bg-white p-8 rounded-[3rem] border-4 border-emerald-100 shadow-inner flex flex-col items-center" data-id="element-813">
                  <div className="relative w-40 h-40 border-4 border-slate-800 rounded-full flex items-center justify-center" data-id="element-814">
                    <div className="absolute top-2 font-black" data-id="element-815">12</div>
                    <div className="absolute right-2 font-black" data-id="element-816">3</div>
                    <div className="absolute bottom-2 font-black" data-id="element-817">6</div>
                    <div className="absolute left-2 font-black" data-id="element-818">9</div>
                    {/* Wijzers */}
                    <div className="absolute w-1 h-16 bg-slate-800 top-4 rounded-full origin-bottom" data-id="element-819" />
                    <div className="absolute w-1.5 h-12 bg-slate-400 top-8 rounded-full origin-bottom rotate-[120deg]" data-id="element-820" />
                    <div className="absolute w-0.5 h-16 bg-rose-500 top-4 rounded-full origin-bottom rotate-[45deg]" data-id="element-821" />
                    <div className="w-3 h-3 bg-slate-900 rounded-full z-10" data-id="element-822" />
                  </div>
                  <div className="mt-6 space-y-2 w-full" data-id="element-823">
                    <div className="flex justify-between text-[10px] font-bold border-b pb-1" data-id="element-824"><span data-id="element-825">Kwart over</span> <span data-id="element-826">15 minuten</span></div>
                    <div className="flex justify-between text-[10px] font-bold border-b pb-1" data-id="element-827"><span data-id="element-828">Half</span> <span data-id="element-829">30 minuten</span></div>
                    <div className="flex justify-between text-[10px] font-bold border-b pb-1" data-id="element-830"><span data-id="element-831">Kwart voor</span> <span data-id="element-832">45 minuten</span></div>
                    <div className="flex justify-between text-[10px] font-black text-rose-500 pt-1" data-id="element-833"><span data-id="element-834">1 minuut</span> <span data-id="element-835">60 seconden</span></div>
                  </div>
                </div>
              </TheoryCard>

              <TheoryCard title="De Digitale Klok (24-uur)" color="teal" data-id="element-836">
                <p className="mb-4" data-id="element-837">Bij de digitale klok tellen we door na 12:00 uur 's middags.</p>
                <div className="bg-slate-900 p-8 rounded-3xl shadow-2xl text-center" data-id="element-838">
                  <div className="text-5xl font-mono font-black text-emerald-400 tracking-tighter" data-id="element-839">16:45:12</div>
                  <div className="flex justify-between mt-4 text-[9px] font-black text-emerald-800 uppercase tracking-widest px-4" data-id="element-840">
                    <span data-id="element-841">Uren</span>
                    <span data-id="element-842">Minuten</span>
                    <span data-id="element-843">Seconden</span>
                  </div>
                </div>
                <div className="mt-8 bg-emerald-100 p-4 rounded-2xl border-2 border-emerald-200" data-id="element-844">
                  <h6 className="font-bold text-emerald-900 text-xs mb-1 uppercase" data-id="element-845">De 'Min 12' Truk:</h6>
                  <p className="text-[11px] text-emerald-800" data-id="element-846">Is het uur hoger dan 12? Haal er 12 vanaf om de middagtijd te weten. <br data-id="element-847" />
                  <strong data-id="element-848">16:45 ➔ 16 - 12 = 4. Dus: kwart voor 5 's middags.</strong></p>
                </div>
              </TheoryCard>
            </div>
          </LessonSection>

          <LessonSection title="2. Tijdsduur Berekenen" icon={TrendingUp} color="teal" data-id="element-849">
            <TheoryCard title="Van Start tot Stop" color="teal" data-id="element-850">
              <p className="mb-6 font-bold italic" data-id="element-851">Vraag: Je begint met zwemmen om 14:45 en je bent klaar om 16:10. Hoe lang heb je gezwommen?</p>
              <div className="space-y-2" data-id="element-852">
                <StepByStepAction color="teal" step={1} title="Aanvullen tot het hele uur" content="Van 14:45 naar 15:00 is 15 minuten." data-id="element-853" />
                <StepByStepAction color="teal" step={2} title="Hele uren tellen" content="Van 15:00 naar 16:00 is precies 1 uur." data-id="element-854" />
                <StepByStepAction color="teal" step={3} title="De rest-minuten" content="Van 16:00 naar 16:10 is 10 minuten." data-id="element-855" />
                <div className="bg-white p-6 rounded-[2rem] border-4 border-teal-500 text-center font-black text-2xl text-teal-900 shadow-xl mt-4" data-id="element-856">
                   TOTAAL: 1 uur en 25 minuten
                </div>
              </div>
            </TheoryCard>
          </LessonSection>

          <LessonSection title="3. Euro's & Centen" icon={Coins} color="amber" data-id="element-857">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-858">
              <TheoryCard title="Geld omrekenen" color="amber" data-id="element-859">
                <div className="space-y-4" data-id="element-860">
                  <div className="bg-white p-4 rounded-2xl border-2 border-amber-200 flex justify-between items-center shadow-sm" data-id="element-861">
                    <span className="font-black text-amber-900 text-lg" data-id="element-862">€ 1,00</span>
                    <ArrowRight className="text-amber-200" data-id="element-863" />
                    <span className="font-mono font-black text-amber-600 text-lg" data-id="element-864">100 cent</span>
                  </div>
                  <ExampleBox data-id="element-865">
                    <div className="text-center w-full" data-id="element-866">
                      <p className="font-bold" data-id="element-867">€ 2,50 is 250 cent</p>
                      <p className="text-[10px] text-slate-400 mt-1 uppercase" data-id="element-868">Verschuif de komma 2 plekken naar rechts</p>
                    </div>
                  </ExampleBox>
                </div>
              </TheoryCard>

              <TheoryCard title="Wisselgeld: De Aanvul-strategie" color="orange" data-id="element-869">
                <p className="text-xs mb-4" data-id="element-870">Je hoeft niet altijd een moeilijke min-som te maken. Tel door vanaf het bedrag!</p>
                <div className="bg-white p-6 rounded-2xl border-2 border-orange-200 shadow-inner space-y-2 font-mono text-xs" data-id="element-871">
                  <div className="flex justify-between text-slate-400" data-id="element-872"><span data-id="element-873">Iets kost: € 6,35</span> <span data-id="element-874">Je geeft: € 10,00</span></div>
                  <div className="h-px bg-slate-100 my-2" data-id="element-875" />
                  <div className="flex justify-between font-bold" data-id="element-876"><span data-id="element-877">6,35 + 5 cent</span> <span data-id="element-878">➔ 6,40</span></div>
                  <div className="flex justify-between font-bold" data-id="element-879"><span data-id="element-880">6,40 + 60 cent</span> <span data-id="element-881">➔ 7,00</span></div>
                  <div className="flex justify-between font-bold text-orange-600" data-id="element-882"><span data-id="element-883">7,00 + 3 euro</span> <span data-id="element-884">➔ 10,00</span></div>
                  <div className="mt-4 pt-4 border-t-2 border-dashed border-orange-100 text-center text-xl font-black text-orange-900" data-id="element-885">
                    Terug: € 3,65
                  </div>
                </div>
              </TheoryCard>
            </div>
          </LessonSection>
        </div>;
    case 'SpellingWW':
      return <div className="max-w-5xl mx-auto py-8" data-id="element-886">
          <header className="mb-12 text-center" data-id="element-887">
            <h2 className="text-5xl font-black text-rose-950 tracking-tighter mb-4 uppercase" data-id="element-888">Werkwoordspelling</h2>
            <p className="text-xl text-slate-500 font-medium italic" data-id="element-889">"Wordt het met een d, een t of allebei?"</p>
          </header>

          <LessonSection title="1. De Basis: De Stam" icon={CheckCircle2} color="rose" data-id="element-890">
            <TheoryCard title="De ik-vorm vinden" color="rose" data-id="element-891">
              <p className="mb-4" data-id="element-892">De stam is de kortste vorm van het werkwoord. Haal <strong data-id="element-893">-en</strong> van het hele werkwoord af.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-894">
                <div className="bg-white p-4 rounded-2xl border border-rose-100 text-center" data-id="element-895">
                  <div className="text-[10px] text-slate-400 uppercase font-black mb-1" data-id="element-896">Standaard</div>
                  <div className="font-mono" data-id="element-897">werk-en ➔ <span className="text-rose-600 font-black" data-id="element-898">werk</span></div>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-rose-100 text-center" data-id="element-899">
                  <div className="text-[10px] text-slate-400 uppercase font-black mb-1" data-id="element-900">Klinkerwissel</div>
                  <div className="font-mono" data-id="element-901">lop-en ➔ <span className="text-rose-600 font-black" data-id="element-902">loop</span></div>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-rose-100 text-center" data-id="element-903">
                  <div className="text-[10px] text-slate-400 uppercase font-black mb-1" data-id="element-904">Dubbelzetter</div>
                  <div className="font-mono" data-id="element-905">bakk-en ➔ <span className="text-rose-600 font-black" data-id="element-906">bak</span></div>
                </div>
              </div>
              <ExceptionWarning title="V en Z wissels" message="De v verandert altijd in een f, en de z in een s. Reizen ➔ reis. Beven ➔ beef." data-id="element-907" />
            </TheoryCard>
          </LessonSection>

          <LessonSection title="2. Tegenwoordige Tijd (Nu)" icon={Calendar} color="purple" data-id="element-908">
            <div className="bg-white rounded-[2.5rem] border-4 border-purple-100 overflow-hidden shadow-2xl" data-id="element-909">
              <table className="w-full text-left text-sm" data-id="element-910">
                <thead className="bg-purple-600 text-white" data-id="element-911">
                  <tr data-id="element-912">
                    <th className="p-6 font-black uppercase tracking-widest" data-id="element-913">Persoon</th>
                    <th className="p-6 font-black uppercase tracking-widest" data-id="element-914">De Regel</th>
                    <th className="p-6 font-black uppercase tracking-widest" data-id="element-915">Voorbeeld</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-purple-50" data-id="element-916">
                  <tr className="hover:bg-purple-50 transition-colors" data-id="element-917">
                    <td className="p-6 font-bold text-purple-900 italic" data-id="element-918">Ik</td>
                    <td className="p-6 font-black text-slate-400 underline decoration-purple-300" data-id="element-919">STAM</td>
                    <td className="p-6 font-mono font-black text-purple-600" data-id="element-920">ik fiets</td>
                  </tr>
                  <tr className="hover:bg-purple-50 transition-colors" data-id="element-921">
                    <td className="p-6 font-bold text-purple-900 italic" data-id="element-922">Jij / Hij / Zij / Het / U</td>
                    <td className="p-6 font-black text-slate-400 underline decoration-purple-300" data-id="element-923">STAM + T</td>
                    <td className="p-6 font-mono font-black text-purple-600" data-id="element-924">hij fiets<span className="text-rose-500 underline" data-id="element-925">t</span></td>
                  </tr>
                  <tr className="hover:bg-purple-50 transition-colors" data-id="element-926">
                    <td className="p-6 font-bold text-purple-900 italic" data-id="element-927">Wij / Jullie / Zij</td>
                    <td className="p-6 font-black text-slate-400 underline decoration-purple-300" data-id="element-928">HELE WERKWOORD</td>
                    <td className="p-6 font-mono font-black text-purple-600" data-id="element-929">wij fietsen</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8" data-id="element-930">
              <TheoryCard title="De Jij-bak regel" color="amber" data-id="element-931">
                <p className="text-xs" data-id="element-932">Staat 'jij' of 'je' <strong data-id="element-933">achter</strong> het werkwoord? Dan schrijf je GEEN t!</p>
                <div className="mt-4 flex gap-4 justify-center" data-id="element-934">
                  <div className="bg-white px-3 py-1 rounded-lg border-2 border-amber-200 line-through text-slate-300 font-mono" data-id="element-935">Bakt jij?</div>
                  <ArrowRight className="text-amber-400" data-id="element-936" />
                  <div className="bg-white px-3 py-1 rounded-lg border-2 border-emerald-400 text-emerald-700 font-black font-mono" data-id="element-937">Bak jij?</div>
                </div>
              </TheoryCard>
              <div className="bg-rose-600 text-white p-6 rounded-[2rem] shadow-xl flex flex-col justify-center" data-id="element-938">
                <h6 className="font-black text-xs uppercase mb-1" data-id="element-939">Heel Belangrijk:</h6>
                <p className="text-xl font-black" data-id="element-940">STAM + T <span className="text-sm font-normal opacity-70 italic" data-id="element-941">geldt óók voor woorden op een d!</span></p>
                <p className="text-xs mt-2 font-mono bg-rose-700 p-2 rounded-xl" data-id="element-942">Worden ➔ hij word + t ➔ wordt</p>
              </div>
            </div>
          </LessonSection>

          <LessonSection title="3. Verleden Tijd (Toen)" icon={Search} color="indigo" data-id="element-943">
            <TheoryCard title="Het Kofschip-X" color="indigo" data-id="element-944">
              <p className="mb-6 text-center text-lg font-bold" data-id="element-945">Kijk naar de laatste letter van de STAM (vóór de klinkerwissel).</p>
              <div className="bg-white p-8 rounded-[3rem] border-4 border-indigo-200 text-center mb-8" data-id="element-946">
                <div className="flex justify-center gap-4 mb-4" data-id="element-947">
                  {['T', 'K', 'F', 'S', 'CH', 'P', 'X'].map(l => <span key={l} className="w-10 h-10 bg-indigo-600 text-white rounded-xl flex items-center justify-center font-black text-xl shadow-lg" data-id="element-948">{l}</span>)}
                </div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest italic" data-id="element-949">De medeklinkers van 't Kofschip-x</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-950">
                <div className="bg-emerald-50 p-6 rounded-3xl border-2 border-emerald-200" data-id="element-951">
                  <h6 className="font-black text-emerald-900 uppercase text-xs mb-2" data-id="element-952">JA, letter zit erin:</h6>
                  <p className="text-xl font-black text-emerald-700 mb-4" data-id="element-953">Stam + TE(N)</p>
                  <ExampleBox data-id="element-954">fiets + te = fietste</ExampleBox>
                </div>
                <div className="bg-blue-50 p-6 rounded-3xl border-2 border-blue-200" data-id="element-955">
                  <h6 className="font-black text-blue-900 uppercase text-xs mb-2" data-id="element-956">NEE, letter zit er niet in:</h6>
                  <p className="text-xl font-black text-blue-700 mb-4" data-id="element-957">Stam + DE(N)</p>
                  <ExampleBox data-id="element-958">wandel + de = wandelde</ExampleBox>
                </div>
              </div>
            </TheoryCard>
          </LessonSection>

          <LessonSection title="4. Voltooid Deelwoord" icon={Star} color="amber" data-id="element-959">
            <TheoryCard title="Het 'Gedaan' Plan" color="amber" data-id="element-960">
              <p className="mb-6" data-id="element-961">Een voltooid deelwoord vertelt dat iets al gedaan is. Het begint vaak met <strong data-id="element-962">ge-</strong>, <strong data-id="element-963">be-</strong> of <strong data-id="element-964">ver-</strong>.</p>
              <div className="flex flex-col md:flex-row items-center justify-center gap-4 bg-white p-8 rounded-[3rem] border-2 border-amber-200 mb-8" data-id="element-965">
                <div className="bg-amber-100 px-4 py-2 rounded-xl font-black text-amber-700" data-id="element-966">GE- / BE- / VER-</div>
                <PlusCircle className="text-slate-200" data-id="element-967" />
                <div className="bg-amber-100 px-4 py-2 rounded-xl font-black text-amber-700" data-id="element-968">STAM</div>
                <PlusCircle className="text-slate-200" data-id="element-969" />
                <div className="bg-amber-500 px-4 py-2 rounded-xl font-black text-white shadow-lg" data-id="element-970">T of D</div>
              </div>
              <p className="text-center text-sm font-bold" data-id="element-971">Hoe weet je of het eindigt op een T of een D? Gebruik weer 't Kofschip op de stam!</p>
            </TheoryCard>
            
            <div className="bg-indigo-900 text-white p-8 rounded-[3rem] shadow-2xl relative overflow-hidden" data-id="element-972">
              <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/5 rounded-full" data-id="element-973" />
              <h6 className="font-black text-xs uppercase mb-4 tracking-widest text-indigo-300" data-id="element-974">Sterke Werkwoorden (Onthouden!)</h6>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 font-mono text-xs" data-id="element-975">
                 <div className="bg-white/10 p-3 rounded-xl border border-white/10" data-id="element-976">Lopen - liep - <span className="text-amber-400 font-bold" data-id="element-977">gelopen</span></div>
                 <div className="bg-white/10 p-3 rounded-xl border border-white/10" data-id="element-978">Eten - at - <span className="text-amber-400 font-bold" data-id="element-979">gegeten</span></div>
                 <div className="bg-white/10 p-3 rounded-xl border border-white/10" data-id="element-980">Schrijven - schreef - <span className="text-amber-400 font-bold" data-id="element-981">geschreven</span></div>
                 <div className="bg-white/10 p-3 rounded-xl border border-white/10" data-id="element-982">Zijn - was - <span className="text-amber-400 font-bold" data-id="element-983">geweest</span></div>
              </div>
            </div>
          </LessonSection>
        </div>;
    case 'SpellingRegel':
      return <div className="max-w-5xl mx-auto py-8" data-id="element-984">
          <header className="mb-12 text-center" data-id="element-985">
            <h2 className="text-5xl font-black text-amber-950 tracking-tighter mb-4 uppercase italic" data-id="element-986">Regelwoorden & Taal</h2>
            <p className="text-xl text-slate-500 font-medium italic" data-id="element-987">"Klinkers stelen en letters verdubbelen."</p>
          </header>

          <LessonSection title="1. Klinkers & Klankgroepen" icon={Type} color="amber" data-id="element-988">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-989">
              <TheoryCard title="De Dubbelzetter" color="amber" data-id="element-990">
                <p className="text-xs mb-4" data-id="element-991">Hoor je een <strong data-id="element-992">korte klank</strong> (a, e, i, o, u) aan het einde van een klankgroep? Dan zet je 2 medeklinkers op de wacht.</p>
                <div className="bg-white p-6 rounded-[2rem] border-4 border-amber-300 text-center text-2xl font-mono font-black" data-id="element-993">
                  ba<span className="text-amber-500 underline decoration-amber-200 underline-offset-8" data-id="element-994">kk</span>er
                </div>
                <div className="mt-4 flex flex-wrap gap-2" data-id="element-995">
                  {['kippen', 'grappig', 'messen', 'stoppen'].map(w => <span key={w} className="text-[10px] bg-amber-100 text-amber-700 px-2 py-1 rounded-lg font-bold" data-id="element-996">{w}</span>)}
                </div>
              </TheoryCard>

              <TheoryCard title="De Klinkerdief" color="sky" data-id="element-997">
                <p className="text-xs mb-4" data-id="element-998">Hoor je een <strong data-id="element-999">lange klank</strong> (aa, ee, oo, uu) aan het einde van een klankgroep? Dan wordt er één letter gestolen door de dief.</p>
                <div className="bg-white p-6 rounded-[2rem] border-4 border-sky-300 text-center text-2xl font-mono font-black" data-id="element-1000">
                  b<span className="text-sky-500 underline decoration-sky-200 underline-offset-8" data-id="element-1001">a</span>-nen
                </div>
                <div className="mt-4 flex flex-wrap gap-2" data-id="element-1002">
                  {['lopen', 'muren', 'spelen', 'jager'].map(w => <span key={w} className="text-[10px] bg-sky-100 text-sky-700 px-2 py-1 rounded-lg font-bold" data-id="element-1003">{w}</span>)}
                </div>
              </TheoryCard>
            </div>
          </LessonSection>

          <LessonSection title="2. Het Trema (¨)" icon={Sparkles} color="indigo" data-id="element-1004">
            <TheoryCard title="Botsende Klinkers" color="indigo" data-id="element-1005">
              <p className="mb-6" data-id="element-1006">Een trema (twee puntjes op de letter) gebruik je als twee klinkers na elkaar staan die je niet als één klank mag lezen. Je begint een nieuwe klankgroep.</p>
              <div className="flex flex-col md:flex-row gap-6 items-center justify-center" data-id="element-1007">
                 <div className="text-center" data-id="element-1008">
                    <div className="text-3xl font-mono font-black text-indigo-700" data-id="element-1009">knieën</div>
                    <div className="text-[10px] text-slate-400 font-bold uppercase mt-1" data-id="element-1010">knie + en</div>
                 </div>
                 <div className="text-center" data-id="element-1011">
                    <div className="text-3xl font-mono font-black text-indigo-700" data-id="element-1012">zeeën</div>
                    <div className="text-[10px] text-slate-400 font-bold uppercase mt-1" data-id="element-1013">zee + en</div>
                 </div>
                 <div className="text-center" data-id="element-1014">
                    <div className="text-3xl font-mono font-black text-indigo-700" data-id="element-1015">belgië</div>
                    <div className="text-[10px] text-slate-400 font-bold uppercase mt-1" data-id="element-1016">belgi + e</div>
                 </div>
              </div>
              <div className="mt-8 bg-indigo-600 text-white p-4 rounded-2xl text-[11px] text-center italic border-2 border-indigo-400 shadow-xl" data-id="element-1017">
                De puntjes staan altijd op de eerste letter van de nieuwe klankgroep!
              </div>
            </TheoryCard>
          </LessonSection>

          <LessonSection title="3. Weetwoorden" icon={Wand2} color="rose" data-id="element-1018">
            <TheoryCard title="Uit je hoofd leren" color="rose" data-id="element-1019">
              <p className="mb-6 text-sm" data-id="element-1020">Sommige woorden hebben geen regel. Die moet je simpelweg onthouden (foto in je hoofd maken!).</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1021">
                 <div className="bg-white p-4 rounded-2xl border-2 border-rose-200 shadow-sm" data-id="element-1022">
                    <h6 className="font-black text-rose-600 text-xs mb-2 uppercase" data-id="element-1023">Café-woorden</h6>
                    <div className="text-2xl font-black" data-id="element-1024">é</div>
                    <p className="text-[10px] text-slate-400" data-id="element-1025">Logé, oké, privè.</p>
                 </div>
                 <div className="bg-white p-4 rounded-2xl border-2 border-rose-200 shadow-sm" data-id="element-1026">
                    <h6 className="font-black text-rose-600 text-xs mb-2 uppercase" data-id="element-1027">Cadeau-woorden</h6>
                    <div className="text-2xl font-black" data-id="element-1028">eau</div>
                    <p className="text-[10px] text-slate-400" data-id="element-1029">Bureau, niveau.</p>
                 </div>
                 <div className="bg-white p-4 rounded-2xl border-2 border-rose-200 shadow-sm" data-id="element-1030">
                    <h6 className="font-black text-rose-600 text-xs mb-2 uppercase" data-id="element-1031">Taxi-woorden</h6>
                    <div className="text-2xl font-black" data-id="element-1032">x <span className="text-xs font-light text-slate-400" data-id="element-1033">(ks)</span></div>
                    <div className="text-[10px] text-slate-400" data-id="element-1034">Tekst, boxer, examen.</div>
                 </div>
              </div>
            </TheoryCard>
          </LessonSection>
        </div>;
    case 'Taalbegrip':
      return <div className="max-w-5xl mx-auto py-8" data-id="element-1035">
          <header className="mb-12 text-center" data-id="element-1036">
            <h2 className="text-5xl font-black text-sky-950 tracking-tighter mb-4 uppercase" data-id="element-1037">Taalbegrip</h2>
            <p className="text-xl text-slate-500 font-medium italic" data-id="element-1038">"Woordsoorten en zinnen ontrafelen."</p>
          </header>

          <LessonSection title="1. De Basis Woordsoorten" icon={BookOpen} color="sky" data-id="element-1039">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1040">
              {[{
              t: "Zelfstandig Naamwoord (zn)",
              d: "Mensen, dieren, planten en dingen waar je de, het of een voor kunt zetten.",
              e: "De bakker, het konijn, de boom, een tafel."
            }, {
              t: "Bijvoeglijk Naamwoord (bn)",
              d: "Zegt iets over een zelfstandig naamwoord. Het geeft meer informatie.",
              e: "De STOUTE bakker, het WITTE konijn."
            }, {
              t: "Werkwoord (ww)",
              d: "Iets wat je kunt doen of wat er gebeurt.",
              e: "Fietsen, lachen, groeien, zijn."
            }, {
              t: "Lidwoord (lw)",
              d: "Er zijn er maar drie in het Nederlands.",
              e: "De, het, een."
            }].map((item, i) => <TheoryCard key={i} title={item.t} color="sky" data-id="element-1041">
                   <p className="text-xs mb-3" data-id="element-1042">{item.d}</p>
                   <div className="bg-white p-3 rounded-xl border border-sky-100 text-sky-600 font-bold text-xs italic" data-id="element-1043">
                     Bv: {item.e}
                   </div>
                </TheoryCard>)}
            </div>
          </LessonSection>

          <LessonSection title="2. Stoffelijk Bijvoeglijk Naamwoord" icon={Box} color="amber" data-id="element-1044">
            <TheoryCard title="Waar is het van gemaakt?" color="amber" data-id="element-1045">
              <p className="mb-4" data-id="element-1046">Een stoffelijk bijvoeglijk naamwoord vertelt van welke <strong data-id="element-1047">stof</strong> iets gemaakt is. Ze eindigen bijna altijd op <strong data-id="element-1048">-en</strong>.</p>
              <div className="flex flex-wrap gap-3" data-id="element-1049">
                 {['Gouden', 'Houten', 'Wollen', 'Zilveren', 'Stenen', 'IJzeren', 'Betonnen'].map(s => <span key={s} className="bg-amber-500 text-white px-4 py-2 rounded-2xl font-black text-sm shadow-lg shadow-amber-200" data-id="element-1050">{s}</span>)}
              </div>
              <ExceptionWarning title="Moderne materialen" message="Woorden voor moderne stoffen eindigen NIET op -en. Je zegt: 'de plastic tas' of 'de aluminium ladder'. Niet plasticen of aluminiumen!" data-id="element-1051" />
            </TheoryCard>
          </LessonSection>

          <LessonSection title="3. De Persoonsvorm (pv)" icon={Search} color="rose" data-id="element-1052">
            <TheoryCard title="De motor van de zin" color="rose" data-id="element-1053">
              <p className="mb-6 font-bold" data-id="element-1054">De persoonsvorm is altijd een werkwoord. Je vindt hem met drie proeven:</p>
              <div className="space-y-4" data-id="element-1055">
                <StepByStepAction color="rose" step={1} title="De Vraagproef" content="Maak de zin vragend. Het eerste woord is de pv. 'Bas loopt.' ➔ 'Loopt Bas?'" data-id="element-1056" />
                <StepByStepAction color="rose" step={2} title="De Tijdproef" content="Zet de zin in een andere tijd (nu naar toen). Het woord dat verandert is de pv. 'Ik ren.' ➔ 'Ik rende.'" data-id="element-1057" />
                <StepByStepAction color="rose" step={3} title="De Getalproef" content="Maak van één persoon meer personen. 'De jongen speelt.' ➔ 'De jongens spelen.'" data-id="element-1058" />
              </div>
            </TheoryCard>
          </LessonSection>
        </div>;
    default:
      return <div className="flex flex-col items-center justify-center py-20 text-slate-300" data-id="element-1059">
          <Lightbulb className="w-20 h-20 mb-4 opacity-20" data-id="element-1060" />
          <p className="text-xl font-bold italic tracking-widest" data-id="element-1061">Selecteer een module om te leren...</p>
        </div>;
  }
}